// Analytics utility for production monitoring
class Analytics {
  constructor() {
    this.isEnabled = process.env.NODE_ENV === 'production' && 
                    import.meta.env.VITE_ENABLE_ANALYTICS === 'true';
    this.events = [];
    this.bandwidthUsage = 0;
  }

  // Track page views
  trackPageView(page) {
    if (!this.isEnabled) return;
    
    this.events.push({
      type: 'pageview',
      page,
      timestamp: Date.now(),
      userAgent: navigator.userAgent
    });
    
    this.sendEvent('pageview', { page });
  }

  // Track user interactions
  trackEvent(eventName, properties = {}) {
    if (!this.isEnabled) return;
    
    this.events.push({
      type: 'event',
      name: eventName,
      properties,
      timestamp: Date.now()
    });
    
    this.sendEvent('event', { name: eventName, properties });
  }

  // Track image loads for bandwidth monitoring
  trackImageLoad(imageId, fileSize) {
    if (!this.isEnabled) return;
    
    this.bandwidthUsage += fileSize || 0;
    
    this.trackEvent('image_load', {
      imageId,
      fileSize,
      totalBandwidth: this.bandwidthUsage
    });
  }

  // Track performance metrics
  trackPerformance(metric, value) {
    if (!this.isEnabled) return;
    
    this.trackEvent('performance', {
      metric,
      value,
      timestamp: Date.now()
    });
  }

  // Track errors
  trackError(error, context = {}) {
    if (!this.isEnabled) return;
    
    this.trackEvent('error', {
      message: error.message,
      stack: error.stack,
      context,
      timestamp: Date.now()
    });
  }

  // Send event to analytics service
  sendEvent(type, data) {
    // In production, you would send this to your analytics service
    // For now, we'll just log it in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`📊 Analytics: ${type}`, data);
    }
    
    // Example: Send to Supabase for analytics
    // this.sendToSupabase(type, data);
  }

  // Send to Supabase (optional)
  async sendToSupabase(type, data) {
    try {
      const { supabase } = await import('./supabase.js');
      await supabase
        .from('analytics_events')
        .insert([{
          type,
          data,
          timestamp: new Date().toISOString()
        }]);
    } catch (error) {
      // Silently fail in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Analytics error:', error);
      }
    }
  }

  // Get analytics summary
  getSummary() {
    return {
      totalEvents: this.events.length,
      bandwidthUsage: this.bandwidthUsage,
      events: this.events.slice(-10) // Last 10 events
    };
  }
}

// Create singleton instance
const analytics = new Analytics();

// Export functions for easy use
export const trackPageView = (page) => analytics.trackPageView(page);
export const trackEvent = (eventName, properties) => analytics.trackEvent(eventName, properties);
export const trackImageLoad = (imageId, fileSize) => analytics.trackImageLoad(imageId, fileSize);
export const trackPerformance = (metric, value) => analytics.trackPerformance(metric, value);
export const trackError = (error, context) => analytics.trackError(error, context);
export const trackBandwidth = (fileSize) => analytics.trackImageLoad('unknown', fileSize);

export default analytics; 